<template>
  <CContainer class="out-of-office-view datatable-container mb-5">
    <Loader :is-processing="isLoading" />

    <CRow class="mt-5">
      <CCol md="12">
        <div
          class="table-tabs-header mb-2 bg-white d-flex align-items-center justify-content-between"
        >
          <div class="d-flex">
            <div v-for="(tab, index) in tableTabs" :key="index">
              <div
                :class="{ active: tab.value === filterParams.activeTab.value }"
                class="table-tab"
                @click="setActiveTableTab(tab)"
              >
                {{ tab.label }}
              </div>
            </div>
          </div>
          <div class="actions d-flex align-items-center">
            <div
              class="lh-normal cursor-pointer text-accent-blue-light"
              @click="handleLocationRestrictions(pagination.activePage, true)"
            >
              <i class="fi flaticon-download me-1" />
              <span class="d-none d-md-inline-block">Download CSV</span>
            </div>

            <div
              class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
            >
              <CInputGroup class="w-auto">
                <div class="input-group-prepend">
                  <span class="input-group-text lh-normal"
                    ><div><i class="flaticon-magnifying-glass"></i></div
                  ></span>
                </div>
                <CFormInput
                  placeholder="Search..."
                  v-model="columnSearchKeys.globalQuery"
                  @input="searchByQuery($event.target.value)"
                >
                </CFormInput>
              </CInputGroup>
            </div>
          </div>
        </div>
        <div
          class="status-guides-container justify-content-between flex-column flex-sm-row align-items-start mb-0 mb-sm-2"
        >
          <div class="d-flex align-items-center mb-3 mb-sm-0">
            <div class="status-list">
              <CCheckBox
                label=""
                class="custom-checkbox"
                v-model="deleteCheckbox"
                name=""
                :inline="true"
                :custom="true"
                :disabled="
                  !locationRestrictions || !locationRestrictions.length
                "
                @update:model-value="toggleAllForDelete"
              />
              <CButton
                :disabled="
                  !locationRestrictions || !locationRestrictions.length
                "
                class="me-2 ms-2 p-small bg-gradient-blue reverse"
                @click="deleteLocationRestriction(null, deleteCollection)"
              >
                <span class="btn bg-white px-2 py-1 px-sm-3 py-sm-2"
                  ><b class="text-graident-blue reverse"
                    >DELETE SELECTED</b
                  ></span
                >
              </CButton>
              <CButton
                :disabled="
                  !locationRestrictions || !locationRestrictions.length
                "
                class="me-2 p-small bg-gradient-blue reverse"
                @click="deleteAllRestrictions()"
              >
                <span class="btn bg-white px-2 py-1 px-sm-3 py-sm-2"
                  ><b class="text-graident-blue reverse">DELETE ALL</b></span
                >
              </CButton>
              <CCheckBox
                label="Student Number"
                v-model="filterParams.student_sis_id"
                name="Student number"
                class="custom-checkbox"
                :inline="true"
                :custom="true"
              />
            </div>
          </div>
          <div>
            <div class="d-flex align-items-end">
              <per-page-selector
                :action="setPerPage"
                :pagination="pagination"
              />
            </div>
          </div>
        </div>
        <DataTable
          v-if="locationRestrictions"
          class="datatable remove-first-th-border bg-white"
          :items="activeLocationRestriction"
          :fields="activeDataFields"
          :sorter="{ external: true }"
          column-filter
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          @update:sorter-value="sortByColumn"
        >
          <template #delete="{ item }">
            <td>
              <CCheckBox
                class="custom-checkbox"
                :inline="true"
                :custom="true"
                :checked="deleteCollection.includes(item.id)"
                @update:model-value="toggleRestrictionForDelete(item)"
              />
            </td>
          </template>
          <template #location="{ item }">
            <td>
              <p class="m-0">
                {{ item.location }}
              </p>
            </td>
          </template>
          <template #location-filter>
            <input
              v-model="columnSearchKeys.location.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>
          <template #student_sis_id="{ item }">
            <td>
              <div>
                {{
                  item.user && item.user.student_sis_id
                    ? item.user.student_sis_id
                    : ""
                }}
              </div>
            </td>
          </template>

          <template #from_date="{ item }">
            <td>
              {{ item.from_date ? item.from_date : "-" }}
            </td>
          </template>
          <template #from_date-filter>
            <input
              v-model="columnSearchKeys.from_date.value"
              placeholder="Search by date"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #to_date="{ item }">
            <td>
              {{ item.to_date ? item.to_date : "-" }}
            </td>
          </template>
          <template #to_date-filter>
            <input
              v-model="columnSearchKeys.to_date.value"
              placeholder="Search by date"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #room="{ item }">
            <td>
              <b v-if="item.room">{{ item.room.name }}</b>
            </td>
          </template>
          <template #room-filter>
            <input
              v-model="columnSearchKeys.room.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #type="{ item }">
            <td>
              <b
                class="limitation-label d-inline-flex align-items-center"
                :class="item.type"
              >
                <i
                  class="me-1"
                  :class="
                    item.type == 'access_denied'
                      ? 'flaticon-forbidden'
                      : 'flaticon-profile-user'
                  "
                />
                {{ item.type ? item.type.replace("_", " ") : "" }}
              </b>
            </td>
          </template>
          <template #type-filter>
            <input
              v-model="columnSearchKeys.type.value"
              placeholder="Search by limitation"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #status="{ item }">
            <td>
              <b
                class="limit-status cursor-pointer no-target"
                :class="item.status ? 'active' : 'inactive-gray'"
                @click="toggleLocationRestriction(item.id)"
                >{{ item.status ? "active" : "inactive" }}</b
              >
            </td>
          </template>
          <template #status-filter>
            <input
              v-model="columnSearchKeys.status.value"
              placeholder="Search by status"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #action="{ item }">
            <td>
              <div class="d-flex">
                <button
                  v-if="item.status"
                  class="btn btn-end me-2 no-target"
                  @click="toggleLocationRestriction(item.id)"
                >
                  end
                </button>
                <button
                  class="btn btn-cancel no-target"
                  @click="deleteLocationRestriction(item.id)"
                >
                  delete
                </button>
              </div>
            </td>
          </template>
          <template #action-filter>
            <div />
          </template>
        </DataTable>

        <Pagination
          v-if="pagination.pages && pagination.pages > 1 && !isLazyLoadingMode"
          class="cs-pagination mt-4"
          :dots="false"
          :double-arrows="true"
          :active-page="pagination.activePage"
          :pages="pagination.pages"
          size="lg"
          align="center"
          @update:active-page="setActivePage"
        >
          <template #last-button>
            <i class="flaticon-right-arrow-1" />
          </template>
          <template #first-button>
            <i class="flaticon-left-arrow-2" />
          </template>
          <template #previous-button>
            <i class="fi flaticon-left-arrow-1" />
          </template>
          <template #next-button>
            <i class="fi flaticon-chevron" />
          </template>
        </Pagination>
      </CCol>
    </CRow>
  </CContainer>
</template>
<script>
import Loader from "@/components/Loader"
import { mapGetters } from "vuex"
import PerPageSelector from "../PerPageSelector.vue"

export default {
  name: "LocationRestrictionsDataTable",
  components: {
    Loader,
    PerPageSelector
  },
  data() {
    return {
      isLazyLoadingMode: false,
      deleteCheckbox: false,
      deleteCollection: [],
      columnSearchKeys: {
        globalQuery: "",
        location: { value: "", column: ["location"], sortBy: "location" },
        student_sis_id: {
          value: "",
          column: ["student_sis_id"],
          sortBy: "student_sis_id"
        },
        from_date: {
          value: "",
          column: ["from_date.raw"],
          sortBy: "from_date"
        },
        to_date: { value: "", column: ["to_date.raw"], sortBy: "to_date" },
        room: { value: "", column: ["room.name"], sortBy: "room.name" },
        type: { value: "", column: ["type"], sortBy: "type" },
        status: { value: "", column: ["status"], sortBy: "status" }
      },
      filterParams: {
        search_query: "",
        sort_query: "",
        activeTab: "",
        student_sis_id: false
      },
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      dataTableFields: [
        { key: "delete", label: "", filter: false, _style: "width:50px" },
        {
          key: "student_sis_id",
          filter: false,
          sorter: false,
          label: "Student number",
          _style: "min-width:150px"
        },
        { key: "location", label: "Student" },
        { key: "from_date", _style: "min-width:120px", label: "Start Date" },
        { key: "to_date", _style: "min-width:120px", label: "End Date" },
        { key: "room", label: "Location" },
        {
          key: "type",
          label: "Limitation",
          _style: "min-width:120px",
          filter: false
        },
        { key: "status", label: "Status", filter: false, sorter: false },
        { key: "action", label: "Action", filter: false, sorter: false }
      ],
      tableTabs: [
        { label: "Active", value: "active", status: 1 },
        { label: "Inactive", value: "inactive", status: 0 }
      ],
      isLoading: false,
      modal: {
        isVisible: {
          edit: false
        },
        activePass: null
      }
    }
  },
  computed: {
    ...mapGetters({
      locationRestrictions: "rooms/locationRestrictions"
    }),
    locationRestrictionsIDCollection() {
      return this.locationRestrictions.map((el) => el.id)
    },
    totalPassesCount() {
      return this.pagination.total > this.locationRestrictions.length
        ? this.pagination.total
        : this.locationRestrictions.length
    },
    activeLocationRestriction() {
      return this.locationRestrictions.filter(
        (item) => item.status == this.filterParams.activeTab.status
      )
    },
    activeDataFields() {
      return this.dataTableFields.filter((field) =>
        !this.filterParams.student_sis_id
          ? field.key !== "student_sis_id"
          : field
      )
    }
  },
  created() {
    this.filterParams.activeTab = this.tableTabs[0]
    this.handleLocationRestrictions()
  },
  mounted() {
    this.initLazyLoading()
  },
  methods: {
    handleLocationRestrictions(page, isExport, noLoader, isLazyLoadingMode) {
      this.isLoading = !noLoader
      this.pagination.activePage = page ? page : 1
      const params = {
        page: this.pagination.activePage,
        per_page: this.pagination.per_page.value
      }
      if (this.filterParams.sort_query) {
        params.sort = this.filterParams.sort_query
      }
      if (this.filterParams.search_query) {
        params.search_query = this.filterParams.search_query
      }
      if (this.filterParams.activeTab) {
        if (this.filterParams.activeTab.value == "active") {
          params.active = true
        }
        if (this.filterParams.activeTab.value == "inactive") {
          params.inactive = true
        }
      }
      if (isExport) {
        this.exportCSVLocationRestrictions(params)
      } else {
        this.getLocationRestrictions(params, noLoader, isLazyLoadingMode)
      }
    },
    getLocationRestrictions(params, noLoader, isLazyLoadingMode) {
      this.isLoading = !noLoader
      this.$store
        .dispatch("rooms/getLocationRestrictions", params)
        .then((response) => {
          const data = response.data
          if (data) {
            if (isLazyLoadingMode) {
              this.$store.commit("rooms/PUSH_LOCATION_RESTRICTIONS", data.data)
            } else {
              this.$store.commit("rooms/SET_LOCATION_RESTRICTIONS", data.data)
            }
            this.pagination.total = data.meta.total
            this.pagination.from = data.meta.from
            this.pagination.to = data.meta.to
            this.pagination.pages = Math.ceil(
              data.meta.total / data.meta.per_page
            )
            this.pagination.activePage = Number(this.pagination.activePage)
            this.pagination = JSON.parse(JSON.stringify(this.pagination))
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    exportCSVLocationRestrictions(params) {
      this.isLoading = true
      this.$store
        .dispatch("rooms/getCSVlocationRestrictionsExport", {
          ...params,
          student_sis_id: this.filterParams.student_sis_id
        })
        .then((response) => {
          if (response.data) {
            this.downloadCSVExport(response.data)
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    downloadCSVExport(data) {
      const csvDataForExport = "data:text/csv;charset=utf-8," + data
      const encodedUri = encodeURI(csvDataForExport)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("target", "_blank")
      link.setAttribute("download", "location-restrictions.csv")
      document.body.appendChild(link)
      link.click()
    },
    searchByQuery(value, type) {
      clearInterval(this.searchTimeOut)
      this.searchTimeOut = setTimeout(() => {
        this.handleQuerySearch(value, type)
      }, 500)
    },
    handleQuerySearch(value, type) {
      const query = value.toString()
      if (
        query.replace(/\s/g, "").length > 0 ||
        query.replace(/\s/g, "").length === 0
      ) {
        this.filterParams.search_query = ""
        if (type && type === "column") {
          this.columnSearchKeys.globalQuery = ""
          for (const key in this.columnSearchKeys) {
            if (Object.hasOwnProperty.call(this.columnSearchKeys, key)) {
              const columnData = this.columnSearchKeys[key]
              if (columnData.value) {
                columnData.column.forEach((col) => {
                  this.filterParams.search_query =
                    this.filterParams.search_query +
                    `${col}:"${columnData.value}", `
                })
              }
            }
          }
          this.filterParams.search_query = this.filterParams.search_query.slice(
            0,
            -2
          )
        } else {
          this.resetSearchQuery()
          this.columnSearchKeys.globalQuery = query
          this.filterParams.search_query = query ? '"' + query + '"' : query
        }
        // this.clearFilterParams();
        this.handleLocationRestrictions()
      }
    },
    sortByColumn(column) {
      const columnName = this.columnSearchKeys[column.column]
        ? this.columnSearchKeys[column.column].sortBy
        : column.column
      this.filterParams.sort_query = `${columnName}:${
        column.asc ? "asc" : "desc"
      }`
      this.handleLocationRestrictions()
    },
    toggleLocationRestriction(id) {
      if (id) {
        this.$store
          .dispatch("rooms/toggleLocationRestriction", id)
          .then((response) => {
            const data = response.data.data
            if (data) {
              this.$store.commit("rooms/UPDATE_LOCATION_RESTRICTION", data)
            }
          })
      }
    },
    deleteAllRestrictions() {
      this.$store
        .dispatch("rooms/deleteAllRestrictions")
        .then(() => {
          this.isLoading = false
          this.deleteCollection = []
          this.deleteCheckbox = false
          this.handleLocationRestrictions()
        })
        .catch(() => (this.isLoading = false))
    },
    deleteLocationRestriction(id, collection) {
      if (id) {
        this.isLoading = true
        this.$store
          .dispatch("rooms/deleteLocationRestriction", id)
          .then(() => {
            this.isLoading = false
            this.pagination.total = this.pagination.total - 1
          })
          .catch(() => (this.isLoading = false))
      }
      if (collection && collection.length) {
        this.isLoading = true
        this.$store
          .dispatch("rooms/deleteLocationRestrictions", collection)
          .then(() => {
            this.handleLocationRestrictions()
            this.deleteCollection = []
            this.deleteCheckbox = false
          })
          .catch(() => (this.isLoading = false))
      }
    },
    setPerPage(option) {
      if (option) {
        this.isLazyLoadingMode = option.label === "All entries"
        this.pagination.per_page = option
        this.handleLocationRestrictions()
      }
    },
    setActivePage(page) {
      this.pagination.activePage = page
      this.handleLocationRestrictions(this.pagination.activePage)
      this.$helpers.scrollToTop()
    },
    setActiveTableTab(tab) {
      this.resetSearchQuery()
      this.filterParams.activeTab = tab
      this.handleLocationRestrictions()
    },
    resetSearchQuery() {
      this.filterParams.search_query = ""
      this.columnSearchKeys = {
        globalQuery: "",
        location: { value: "", column: ["location"], sortBy: "location" },
        student_sis_id: {
          value: "",
          column: ["student_sis_id"],
          sortBy: "student_sis_id"
        },
        from_date: {
          value: "",
          column: ["from_date.raw"],
          sortBy: "from_date"
        },
        to_date: { value: "", column: ["to_date.raw"], sortBy: "to_date" },
        room: { value: "", column: ["room.name"], sortBy: "room.name" },
        type: { value: "", column: ["type"], sortBy: "type" },
        status: { value: "", column: ["status"], sortBy: "status" }
      }
    },
    resetParams() {
      this.filterParams = {
        search_query: "",
        sort_query: "",
        activeTab: this.tableTabs[0]
      }
    },
    refreshDataTable() {
      this.resetParams()
      this.resetSearchQuery()
      this.handleLocationRestrictions(1, false, true)
    },
    toggleAllForDelete() {
      this.deleteCollection = this.deleteCheckbox
        ? this.locationRestrictionsIDCollection
        : []
    },
    toggleRestrictionForDelete(item) {
      // if(event.target.classList && !event.target.classList.contains('no-target')){
      if (
        this.deleteCollection.length &&
        this.deleteCollection.includes(item.id)
      ) {
        this.deleteCollection = this.deleteCollection.filter(
          (el) => el != item.id
        )
      } else {
        this.deleteCollection.push(item.id)
      }
      // }
    },
    initLazyLoading() {
      const body = document.getElementsByClassName("main-scroll-container")[0]
      if (body) {
        body.onscroll = () => {
          if (
            this.isLazyLoadingMode &&
            this.locationRestrictions &&
            this.locationRestrictions.length &&
            this.pagination.total > this.locationRestrictions.length
          ) {
            if (body.offsetHeight + body.scrollTop + 1 >= body.scrollHeight) {
              this.pagination.activePage = this.pagination.activePage + 1
              this.handleLocationRestrictions(
                this.pagination.activePage,
                false,
                false,
                true
              )
            }
          }
        }
      }
    }
  }
}
</script>
